export type ParsedStat = {
    prefix: string;
    suffix: string;
    value: number;
    decimals: number;
    useGrouping: boolean;
    isTime: boolean;
};

const STAT_NUMBER =
    /^(\D*?)(\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?)(.*)$/;

function formatGroupedNumber(
    value: number,
    decimals: number,
    useGrouping: boolean
) {
    const fixed = value.toFixed(decimals);
    if (!useGrouping) return fixed;

    const negative = fixed.startsWith("-");
    const unsigned = negative ? fixed.slice(1) : fixed;
    const [intPart, decPart] = unsigned.split(".");
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const body = decPart !== undefined ? `${grouped}.${decPart}` : grouped;
    return negative ? `-${body}` : body;
}

function formatMinutes(value: number) {
    const minutes = Math.floor(value);
    const seconds = Math.round((value - minutes) * 60);
    return `${minutes}m${seconds}s`;
}

export function visibleStatText(value: unknown): string | null {
    if (typeof value !== "string") return null;
    return value.trim() ? value : null;
}

export function parseStatValue(
    input: string | null | undefined
): ParsedStat | null {
    if (typeof input !== "string" || input.trim() === "") return null;

    if (input.includes("min")) {
        const match = input.match(/(\d+(?:\.\d+)?)/);
        if (!match) return null;
        const value = Number(match[1]);
        if (!Number.isFinite(value)) return null;
        return {
            prefix: "",
            suffix: "",
            value,
            decimals: 0,
            useGrouping: false,
            isTime: true,
        };
    }

    const match = input.match(STAT_NUMBER);
    if (!match) return null;

    const prefix = match[1];
    const numericText = match[2];
    const suffix = match[3];
    const value = Number(numericText.replace(/,/g, ""));
    if (!Number.isFinite(value)) return null;

    const decimalMatch = numericText.match(/\.(\d+)$/);
    return {
        prefix,
        suffix,
        value,
        decimals: decimalMatch ? decimalMatch[1].length : 0,
        useGrouping: numericText.includes(","),
        isTime: false,
    };
}

export function formatCountingStat(
    input: string,
    current: number,
    complete: boolean
) {
    const parts = parseStatValue(input);
    if (!parts) return typeof input === "string" ? input : "";

    if (parts.isTime) {
        const source = complete
            ? parts.value
            : Math.min(Math.max(current, 0), parts.value);
        return formatMinutes(source);
    }

    if (complete) return input;

    const bounded = Math.min(Math.max(current, 0), parts.value);
    return `${parts.prefix}${formatGroupedNumber(
        bounded,
        parts.decimals,
        parts.useGrouping
    )}${parts.suffix}`;
}
